import React, { useState } from 'react';
import { Check, Pencil, Plus } from 'lucide-react';
import { PlanPicker, planTotal, BASIC_FEATURES } from './PlanWizard';
import { PaymentTermsCheck } from './PaymentTermsCheck';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function PlanReview({ plan, setPlan, pricing, addons, declaration, setDeclaration, terms, setTerms, coupon, setCoupon }) {
  const [editing, setEditing] = useState(false);
  const isAll = plan.product_type === 'all_access_1y';
  const chosen = addons.filter((a) => plan.addons.includes(a.key));
  const notChosen = isAll ? [] : addons.filter((a) => !plan.addons.includes(a.key));
  const subtotal = planTotal(plan, pricing, addons);
  const gst = Math.round(subtotal * (pricing.gst_rate || 0) / 100);

  return (
    <div className="mt-8 space-y-6" data-testid="plan-review">
      <div className="rounded-xl border border-zinc-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Your plan</h3>
          <button type="button" onClick={() => setEditing(!editing)} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="plan-review-change">
            <Pencil className="w-3.5 h-3.5" /> {editing ? 'Done' : 'Change'}
          </button>
        </div>
        {editing ? (
          <PlanPicker pricing={pricing} addons={addons} plan={plan} setPlan={setPlan} />
        ) : (
          <>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-zinc-300"><span>{pricing.products[plan.product_type].name}</span><span>{inr(pricing.products[plan.product_type].inr)}</span></div>
              {!isAll && chosen.map((a) => <div key={a.key} className="flex justify-between text-zinc-400"><span>+ {a.name}</span><span>{inr(a.price_inr)}</span></div>)}
              <div className="flex justify-between text-zinc-500"><span>GST ({pricing.gst_rate}%)</span><span>{inr(gst)}</span></div>
              <div className="flex justify-between text-white font-bold pt-2 border-t border-zinc-800"><span>Total</span><span data-testid="plan-review-total">{inr(subtotal + gst)}</span></div>
            </div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-1.5">
              {[...BASIC_FEATURES, ...(isAll ? addons.map((a) => a.name) : chosen.map((a) => a.name))].map((f) => (
                <li key={f} className="text-xs text-zinc-400 flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{f}</li>
              ))}
            </ul>
            {notChosen.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-800 flex flex-wrap gap-2" data-testid="plan-review-addons">
                {notChosen.map((a) => (
                  <button key={a.key} type="button" onClick={() => setPlan({ ...plan, addons: [...plan.addons, a.key] })} className="text-xs border border-zinc-700 hover:border-[#C41E3A] text-zinc-300 rounded-full px-3 py-1.5 flex items-center gap-1" data-testid={`plan-review-add-${a.key}`}>
                    <Plus className="w-3 h-3" /> {a.name} for {inr(a.price_inr)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Promo code (optional)" className="mt-4 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-sm uppercase placeholder-zinc-600" data-testid="plan-review-coupon" />
      </div>

      <div className="rounded-xl border border-zinc-700 p-5 space-y-4" data-testid="publish-consents">
        <label className="flex items-start gap-3 cursor-pointer text-sm text-zinc-300">
          <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} className="mt-0.5 w-4 h-4 shrink-0 accent-[#C41E3A]" data-testid="publish-declaration-checkbox" />
          <span>I confirm the information in this profile is accurate, I am authorised to represent this company, and I have the rights to the logo, images and videos used. I understand some content is AI-assisted and that I'm responsible for reviewing it.<span className="text-[#C41E3A]"> *</span></span>
        </label>
        <PaymentTermsCheck checked={terms} onChange={setTerms} testId="publish-payment-terms" />
      </div>
    </div>
  );
}
